import { useAppStore } from '@store/appStore';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './SettingsPanel.css';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SettingsPanel({ isOpen, onClose }: SettingsPanelProps) {
  const { t } = useTranslation('viewer');
  const settings = useAppStore((state) => state.settings);
  const updateSettings = useAppStore((state) => state.updateSettings);
  const toggleDebugOverlay = useAppStore((state) => state.toggleDebugOverlay);
  const toggleMeshViewer = useAppStore((state) => state.toggleMeshViewer);
  const showDebugOverlay = useAppStore((state) => state.showDebugOverlay);
  const showMeshViewer = useAppStore((state) => state.showMeshViewer);
  
  // Phase 2: Pixel optimization state
  const hasPixelData = useAppStore((state) => state.hasPixelData);
  const pixelCacheStats = useAppStore((state) => state.pixelCacheStats);
  
  // Dataset management
  const availableDatasets = useAppStore((state) => state.availableDatasets);
  const currentDatasetId = useAppStore((state) => state.currentDatasetId);
  const loadDataset = useAppStore((state) => state.loadDataset);
  const isLoadingData = useAppStore((state) => state.isLoadingData);
  
  const [selectedDatasetId, setSelectedDatasetId] = useState<string>(currentDatasetId || '');
  
  useEffect(() => {
    if (currentDatasetId) {
      setSelectedDatasetId(currentDatasetId);
    }
  }, [currentDatasetId]);
  
  const handleTransitionDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    updateSettings({ transitionDuration: value });
  };
  
  const handleSensitivityChange = (sensitivity: 'low' | 'medium' | 'high') => {
    updateSettings({ gestureSensitivity: sensitivity });
  };
  
  const handleDebugToggle = () => {
    toggleDebugOverlay();
  };

  const handleMeshViewerToggle = () => {
    toggleMeshViewer();
  };
  
  const handleRaycastModeChange = (mode: 'projection' | 'pixels' | 'auto') => {
    console.log(`⚙️  [SettingsPanel] Raycast mode changed:`, {
      from: settings.raycastMode,
      to: mode,
      hasPixelData,
      pixelCacheSize: pixelCacheStats?.cacheSize || 0
    })
    updateSettings({ raycastMode: mode });
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="settings-panel__backdrop" 
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Settings Panel */}
      <div className="settings-panel">
        <div className="settings-panel__header">
          <h2>{t('settings')}</h2>
          <button 
            className="settings-panel__close"
            onClick={onClose}
            aria-label={t('closeSettings')}
          >
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        
        <div className="settings-panel__content">
          {/* Dataset Selector */}
          <div className="settings-panel__section">
            <label className="settings-panel__label">
              {t('dataset')}
            </label>
            {availableDatasets.length > 0 ? (
              <>
                <select
                  className="settings-panel__select"
                  value={selectedDatasetId}
                  onChange={(e) => setSelectedDatasetId(e.target.value)}
                  disabled={isLoadingData}
                >
                  <option value="">{t('selectDataset')}</option>
                  {availableDatasets.map((dataset) => (
                    <option key={dataset.id} value={dataset.id}>
                      {dataset.name} ({dataset.camera_count} cameras, {dataset.success_rate})
                    </option>
                  ))}
                </select>
                <button
                  className="settings-panel__button"
                  onClick={() => selectedDatasetId && loadDataset(selectedDatasetId)}
                  disabled={isLoadingData || !selectedDatasetId || selectedDatasetId === currentDatasetId}
                >
                  {isLoadingData ? t('common:loadingDots') : t('loadDataset')}
                </button>
                {currentDatasetId && (
                  <p className="settings-panel__description">
                    {t('currentDataset', { name: availableDatasets.find(d => d.id === currentDatasetId)?.name })}
                  </p>
                )}
              </>
            ) : (
              <p className="settings-panel__description">
                {t('noDatasetsAvailable')}
              </p>
            )}
          </div>
          
          {/* Transition Duration Slider */}
          <div className="settings-panel__section">
            <label className="settings-panel__label">
              {t('transitionDuration')}
              <span className="settings-panel__value">{settings.transitionDuration}ms</span>
            </label>
            <input
              type="range"
              min="200"
              max="800"
              step="50"
              value={settings.transitionDuration}
              onChange={handleTransitionDurationChange}
              className="settings-panel__slider"
            />
            <div className="settings-panel__slider-labels">
              <span>{t('transitionFast')}</span>
              <span>{t('transitionSlow')}</span>
            </div>
          </div>
          
          {/* Gesture Sensitivity */}
          <div className="settings-panel__section">
            <label className="settings-panel__label">
              {t('gestureSensitivity')}
            </label>
            <div className="settings-panel__radio-group">
              <button
                className={`settings-panel__radio-button ${settings.gestureSensitivity === 'low' ? 'active' : ''}`}
                onClick={() => handleSensitivityChange('low')}
              >
                <div className="settings-panel__radio-circle">
                  {settings.gestureSensitivity === 'low' && (
                    <div className="settings-panel__radio-dot" />
                  )}
                </div>
                <span>{t('low')}</span>
              </button>
              
              <button
                className={`settings-panel__radio-button ${settings.gestureSensitivity === 'medium' ? 'active' : ''}`}
                onClick={() => handleSensitivityChange('medium')}
              >
                <div className="settings-panel__radio-circle">
                  {settings.gestureSensitivity === 'medium' && (
                    <div className="settings-panel__radio-dot" />
                  )}
                </div>
                <span>{t('medium')}</span>
              </button>
              
              <button
                className={`settings-panel__radio-button ${settings.gestureSensitivity === 'high' ? 'active' : ''}`}
                onClick={() => handleSensitivityChange('high')}
              >
                <div className="settings-panel__radio-circle">
                  {settings.gestureSensitivity === 'high' && (
                    <div className="settings-panel__radio-dot" />
                  )}
                </div>
                <span>{t('high')}</span>
              </button>
            </div>
            <p className="settings-panel__description">
              {t('sensitivityDescription')}
            </p>
          </div>
          
          {/* Raycast Mode (Phase 2: Pixel Optimization) */}
          <div className="settings-panel__section">
            <label className="settings-panel__label">
              {t('raycastMethod')} {hasPixelData && <span className="settings-panel__badge">v2.0</span>}
            </label>
            <div className="settings-panel__radio-group">
              <button
                className={`settings-panel__radio-button ${settings.raycastMode === 'projection' ? 'active' : ''}`}
                onClick={() => handleRaycastModeChange('projection')}
              >
                <div className="settings-panel__radio-circle">
                  {settings.raycastMode === 'projection' && (
                    <div className="settings-panel__radio-dot" />
                  )}
                </div>
                <span>{t('projection')}</span>
              </button>
              
              <button
                className={`settings-panel__radio-button ${settings.raycastMode === 'pixels' ? 'active' : ''}`}
                onClick={() => handleRaycastModeChange('pixels')}
                disabled={!hasPixelData}
              >
                <div className="settings-panel__radio-circle">
                  {settings.raycastMode === 'pixels' && (
                    <div className="settings-panel__radio-dot" />
                  )}
                </div>
                <span>{hasPixelData ? t('pixels') : t('pixelsNA')}</span>
              </button>
              
              <button
                className={`settings-panel__radio-button ${settings.raycastMode === 'auto' ? 'active' : ''}`}
                onClick={() => handleRaycastModeChange('auto')}
              >
                <div className="settings-panel__radio-circle">
                  {settings.raycastMode === 'auto' && (
                    <div className="settings-panel__radio-dot" />
                  )}
                </div>
                <span>{t('auto')}</span>
              </button>
            </div>
            <p className="settings-panel__description">
              {settings.raycastMode === 'projection' && t('projectionDesc')}
              {settings.raycastMode === 'pixels' && hasPixelData && t('pixelsDesc', { count: pixelCacheStats?.cacheSize || 0 })}
              {settings.raycastMode === 'pixels' && !hasPixelData && t('pixelsRequiresV2')}
              {settings.raycastMode === 'auto' && t('autoDesc')}
            </p>
            {pixelCacheStats && settings.raycastMode !== 'projection' && (
              <div className="settings-panel__stats">
                <div className="settings-panel__stat">
                  <span className="settings-panel__stat-label">{t('cacheSize')}</span>
                  <span className="settings-panel__stat-value">{pixelCacheStats.cacheSize} points</span>
                </div>
                <div className="settings-panel__stat">
                  <span className="settings-panel__stat-label">{t('buildTime')}</span>
                  <span className="settings-panel__stat-value">{pixelCacheStats.buildTime.toFixed(2)}ms</span>
                </div>
                <div className="settings-panel__stat">
                  <span className="settings-panel__stat-label">{t('hitRate')}</span>
                  <span className="settings-panel__stat-value">
                    {((pixelCacheStats.hits / Math.max(1, pixelCacheStats.hits + pixelCacheStats.misses)) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Mesh Viewer Toggle */}
          <div className="settings-panel__section">
            <label className="settings-panel__label">
              {t('meshViewer')}
            </label>
            <button
              className={`settings-panel__toggle ${showMeshViewer ? 'active' : ''}`}
              onClick={handleMeshViewerToggle}
              aria-pressed={showMeshViewer}
            >
              <div className="settings-panel__toggle-track">
                <div className="settings-panel__toggle-thumb" />
              </div>
              <span>{showMeshViewer ? t('common:on') : t('common:off')}</span>
            </button>
            <p className="settings-panel__description">
              {t('meshViewerDescription')}
            </p>
          </div>
          
          {/* Advanced Settings - Collapsible */}
          <details className="settings-panel__section settings-panel__advanced">
            <summary className="settings-panel__label settings-panel__summary">
              {t('advancedSettings')}
            </summary>
            
            <div className="settings-panel__advanced-content">
              {/* DZ Filter Toggle */}
              <div className="settings-panel__subsection">
                <label className="settings-panel__label">
                  {t('depthChangeFilter')}
                  <span className="settings-panel__badge-new">{t('newBadge')}</span>
                </label>
                <button
                  className={`settings-panel__toggle ${settings.enableDzFilter ? 'active' : ''}`}
                  onClick={() => updateSettings({ enableDzFilter: !settings.enableDzFilter })}
                  aria-pressed={settings.enableDzFilter}
                >
                  <div className="settings-panel__toggle-track">
                    <div className="settings-panel__toggle-thumb" />
                  </div>
                  <span>{settings.enableDzFilter ? t('common:on') : t('common:off')}</span>
                </button>
                <p className="settings-panel__description">
                  {settings.enableDzFilter 
                    ? t('dzFilterOnDesc')
                    : t('dzFilterOffDesc')}
                </p>
              </div>
              
              {/* Frame Blur Toggle */}
              <div className="settings-panel__subsection">
                <label className="settings-panel__label">
                  {t('backgroundFrameBlur')}
                </label>
                <button
                  className={`settings-panel__toggle ${settings.enableFrameBlur ? 'active' : ''}`}
                  onClick={() => updateSettings({ enableFrameBlur: !settings.enableFrameBlur })}
                  aria-pressed={settings.enableFrameBlur}
                >
                  <div className="settings-panel__toggle-track">
                    <div className="settings-panel__toggle-thumb" />
                  </div>
                  <span>{settings.enableFrameBlur ? t('common:on') : t('common:off')}</span>
                </button>
                <p className="settings-panel__description">
                  {t('blurDescription')}
                </p>
              </div>
              
              {/* Distance Coverage Toggle */}
              <div className="settings-panel__subsection">
                <label className="settings-panel__label">
                  {t('distanceCoverage')}
                </label>
                <button
                  className={`settings-panel__toggle ${settings.useDistanceCoverage ? 'active' : ''}`}
                  onClick={() => updateSettings({ useDistanceCoverage: !settings.useDistanceCoverage })}
                  aria-pressed={settings.useDistanceCoverage}
                >
                  <div className="settings-panel__toggle-track">
                    <div className="settings-panel__toggle-thumb" />
                  </div>
                  <span>{settings.useDistanceCoverage ? t('common:on') : t('common:off')}</span>
                </button>
                <p className="settings-panel__description">
                  {t('distanceCoverageDesc')}
                </p>
              </div>
              
              {/* Stage 2 Intelligent Selection Toggle */}
              <div className="settings-panel__subsection">
                <label className="settings-panel__label">
                  {t('stage2Selection')}
                  <span className="settings-panel__badge-new">{t('experimentalBadge')}</span>
                </label>
                <button
                  className={`settings-panel__toggle ${!settings.useOldPanBehavior ? 'active' : ''}`}
                  onClick={() => updateSettings({ useOldPanBehavior: !settings.useOldPanBehavior })}
                  aria-pressed={!settings.useOldPanBehavior}
                >
                  <div className="settings-panel__toggle-track">
                    <div className="settings-panel__toggle-thumb" />
                  </div>
                  <span>{!settings.useOldPanBehavior ? t('stage2On') : t('stage2Off')}</span>
                </button>
                <p className="settings-panel__description">
                  {!settings.useOldPanBehavior 
                    ? t('stage2OnDesc')
                    : t('stage2OffDesc')}
                </p>
              </div>
            </div>
          </details>
          
          {/* Info Section */}
          <div className="settings-panel__section settings-panel__info">
            <h3>{t('about')}</h3>
            <p>
              {t('aboutDescription')}
            </p>
            <p className="settings-panel__version">
              {t('version')}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
